import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getYear, getMonth } from 'date-fns';
import { ChevronDownIcon, CurrencyDollarIcon, LightBulbIcon, PlusIcon } from '@heroicons/react/24/solid';
import { fetchBudgetsWithTransactions, fetchTransactionsForBudget, deleteTransaction } from '../api/budgets';
import { Budget } from '../components/BudgetItem';
import LoadingSpinner from '../components/LoadingSpinner';
import { TransactionOverlay } from '../components/TransactionOverlay';
import { EditTransactionOverlay } from '../components/EditTransactionOverlay';
import { QuickAddOverlay } from '../components/QuickAddOverlay';
import ConfirmDialog from '../components/ConfirmDialog';

const currentYear = getYear(new Date());
const currentMonth = getMonth(new Date());
const years = [currentYear, ...Array.from({ length: 10 }, (_, i) => currentYear - i - 1)];
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface Transaction {
  _id: string;
  amount: number;
  datetime: string;
  note: string;
  budget?: string;
  transactionType?: 'expense' | 'savings';
}

function NativePillSelect({ 
  value, 
  options, 
  onChange, 
  className = "" 
}: { 
  value: string; 
  options: string[]; 
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <div className={`relative flex-1 ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-gray-200 rounded-full px-6 py-2.5 text-base font-semibold text-gray-800 focus:outline-none select-none text-center cursor-pointer"
        style={{
          backgroundImage: 'none',
          paddingRight: '2.5rem',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          touchAction: 'manipulation'
        }}
      >
        {options.map(option => (
          <option key={option} value={option} className="text-left py-2 px-4">
            {option}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
    </div>
  );
}

function TransactionItem({ transaction, onClick }: { transaction: Transaction; onClick: () => void }) {
  const date = new Date(transaction.datetime);
  const formattedDate = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
  const formattedTime = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });

  return (
    <div 
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="text-sm text-gray-500">
            {formattedDate} at {formattedTime}
          </div>
          {transaction.note && (
            <div className="text-gray-700 text-sm mt-1">
              {transaction.note}
            </div>
          )}
        </div>
        <div className="text-lg font-semibold text-gray-900 ml-4">
          ${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>
    </div>
  );
}

export function BudgetDetails() {
  const { budgetId } = useParams<{ budgetId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get year and month from URL parameters, fallback to current date
  const urlYear = searchParams.get('year');
  const urlMonth = searchParams.get('month');
  const initialYear = urlYear ? parseInt(urlYear) : currentYear;
  const initialMonth = urlMonth ? parseInt(urlMonth) : currentMonth;

  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [budget, setBudget] = useState<Budget | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditOverlay, setShowEditOverlay] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [activeTab, setActiveTab] = useState<'transactions' | 'insights'>('transactions');

  // Load budget and transactions for the selected year/month
  const loadBudgetAndTransactions = useCallback(async (selectedYear: number, selectedMonth: number) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch budgets with transactions
      const fetchedBudgets = await fetchBudgetsWithTransactions(selectedYear, selectedMonth);
      
      // Find the specific budget
      const foundBudget = fetchedBudgets.find(b => b._id === budgetId);
      if (!foundBudget) {
        setError('Budget not found');
        return;
      }

      // Transform budget data
      const transformedBudget: Budget = {
        id: foundBudget._id,
        name: foundBudget.name,
        icon: foundBudget.icon,
        amount: foundBudget.amount,
        spent: foundBudget.spent || 0,
        budgetType: foundBudget.budgetType,
        savingsGoal: foundBudget.savingsGoal,
        targetDate: foundBudget.targetDate,
        expensePeriodValue: foundBudget.expensePeriodValue,
        expensePeriodUnit: foundBudget.expensePeriodUnit,
        // Store additional data for Expense Fund calculations
        availableAmount: (foundBudget as any).availableAmount || 0,
        expenseAmount: (foundBudget as any).expenseAmount || 0,
        savingsAmount: (foundBudget as any).savingsAmount || 0
      };
      
      setBudget(transformedBudget);
      
      // Fetch transactions for this budget
      const fetchedTransactions = await fetchTransactionsForBudget(budgetId!, selectedYear, selectedMonth);
      setTransactions(fetchedTransactions);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load budget details');
    } finally {
      setLoading(false);
    }
  }, [budgetId]);

  // Load data on component mount
  useEffect(() => {
    if (budgetId) {
      loadBudgetAndTransactions(year, month);
    }
  }, [budgetId, loadBudgetAndTransactions, year, month]);

  // Reload data when year or month changes
  useEffect(() => {
    if (budgetId) {
      loadBudgetAndTransactions(year, month);
    }
  }, [year, month, budgetId, loadBudgetAndTransactions]);

  // Calculate budget progress
  let remaining = 0;
  let percentageRemaining = 0;
  let isOverspent = false;
  
  if (budget) {
    if (budget.budgetType === 'Savings') {
      // For Savings budgets, use monthly saved amount for progress bar
      const monthlySaved = budget.savingsAmount || 0;
      remaining = budget.amount - monthlySaved;
      percentageRemaining = budget.amount > 0 ? (monthlySaved / budget.amount) * 100 : 0;
      isOverspent = false; // Savings budgets don't have overspending concept
    } else if (budget.budgetType === 'Expense Fund') {
      // Check if viewing from savings page or home page
      const source = searchParams.get('source');
      
      if (source === 'savings') {
        // For Expense Fund budgets viewed from savings page, calculate overall progress
        const totalSavings = budget.savingsAmount || 0;
        const totalExpenses = budget.expenseAmount || 0;
        const netSaved = totalSavings - totalExpenses;
        
        // Calculate remaining amount: total period amount - net saved
        remaining = budget.amount - netSaved;
        
        // For percentage calculation, use the total period amount as the "budget"
        percentageRemaining = budget.amount > 0 ? (netSaved / budget.amount) * 100 : 0;
      } else {
        // For Expense Fund budgets viewed from home page, calculate total savings - total expenses
        const totalSavings = budget.savingsAmount || 0;
        const totalExpenses = budget.expenseAmount || 0;
        
        // Calculate available amount: total savings - total expenses
        remaining = totalSavings - totalExpenses;
        
        // For percentage calculation, use the total period amount as the "budget"
        percentageRemaining = budget.amount > 0 ? (remaining / budget.amount) * 100 : 0;
      }
      
      isOverspent = false; // Expense Fund budgets don't have overspending concept
    } else {
      // Regular budget calculation
      remaining = budget.amount - budget.spent;
      percentageRemaining = budget.amount > 0 ? (remaining / budget.amount) * 100 : 0;
      isOverspent = budget.spent > budget.amount;
    }
  }

  // Determine progress bar color
  let progressColor = 'bg-green-500';
  if (budget?.budgetType === 'Expense Fund') {
    // For Expense Fund budgets, use red if negative, green if positive
    progressColor = remaining < 0 ? 'bg-red-500' : 'bg-green-500';
  } else if (isOverspent) {
    progressColor = 'bg-red-500';
  }
  else if (percentageRemaining <= 15) {
    progressColor = 'bg-yellow-500';
  }

  // Filter transactions based on search term and budget type/source
  let filteredTransactions = transactions.filter(transaction =>
    transaction.note.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transaction.amount.toString().includes(searchTerm)
  );

  // For Expense Fund budgets, filter transactions based on source
  if (budget?.budgetType === 'Expense Fund') {
    if (searchParams.get('source') === 'savings') {
      // From savings page, only show savings transactions
      filteredTransactions = filteredTransactions.filter(transaction => 
        transaction.transactionType === 'savings'
      );
    } else {
      // From home page, only show expense transactions (and legacy ones)
      filteredTransactions = filteredTransactions.filter(transaction => 
        !transaction.transactionType || transaction.transactionType === 'expense'
      );
    }
  }

  // Filter transactions for insights based on source page
  const getInsightsTransactions = () => {
    if (budget?.budgetType === 'Expense Fund') {
      const source = searchParams.get('source');
      if (source === 'savings') {
        // From savings page, only show savings transactions
        return transactions.filter(transaction => 
          transaction.transactionType === 'savings'
        );
      } else {
        // From home page, only show expense transactions (and legacy ones)
        return transactions.filter(transaction => 
          !transaction.transactionType || transaction.transactionType === 'expense'
        );
      }
    }
    // For other budget types, use all transactions
    return transactions;
  };

  const insightsTransactions = getInsightsTransactions();

  // Calculate insights
  const getMostCommonNote = () => {
    if (insightsTransactions.length === 0) return { note: 'None', totalAmount: 0, count: 0 };

    // Group transactions by note and calculate both count and total amount
    const noteStats = insightsTransactions.reduce((acc, transaction) => {
      const note = transaction.note || '';
      if (!acc[note]) {
        acc[note] = { count: 0, totalAmount: 0 };
      }
      acc[note].count += 1;
      acc[note].totalAmount += transaction.amount;
      return acc;
    }, {} as Record<string, { count: number; totalAmount: number }>);

    // Find the note with highest frequency (must appear more than once)
    let maxCount = 0;
    let candidates: Array<{ note: string; totalAmount: number; count: number }> = [];

    for (const [note, stats] of Object.entries(noteStats)) {
      if (stats.count > 1) { // Only consider notes that appear more than once
        if (stats.count > maxCount) {
          maxCount = stats.count;
          candidates = [{ note, totalAmount: stats.totalAmount, count: stats.count }];
        } else if (stats.count === maxCount) {
          candidates.push({ note, totalAmount: stats.totalAmount, count: stats.count });
        }
      }
    }

    if (candidates.length === 0) return { note: 'None', totalAmount: 0, count: 0 };

    // If multiple candidates with same count, choose the one with highest total amount
    if (candidates.length > 1) {
      candidates.sort((a, b) => b.totalAmount - a.totalAmount);
    }

    return { note: candidates[0].note, totalAmount: candidates[0].totalAmount, count: candidates[0].count };
  };

  const getHighestValueTransaction = () => {
    if (insightsTransactions.length === 0) return null;
    return insightsTransactions.reduce((max, transaction) =>
      transaction.amount > max.amount ? transaction : max
    );
  };

  const getHighestCollectiveTransactions = () => {
    if (insightsTransactions.length === 0) return { note: 'None', totalAmount: 0, count: 0 };

    // Group transactions by note and calculate total amounts
    const noteStats = insightsTransactions.reduce((acc, transaction) => {
      const note = transaction.note || '';
      if (!acc[note]) {
        acc[note] = { count: 0, totalAmount: 0 };
      }
      acc[note].count += 1;
      acc[note].totalAmount += transaction.amount;
      return acc;
    }, {} as Record<string, { count: number; totalAmount: number }>);

    // Find the note with the highest total amount
    let highestTotal = 0;
    let highestNote = '';
    let highestCount = 0;

    for (const [note, stats] of Object.entries(noteStats)) {
      if (stats.totalAmount > highestTotal) {
        highestTotal = stats.totalAmount;
        highestNote = note;
        highestCount = stats.count;
      }
    }

    return {
      note: highestNote || 'None',
      totalAmount: highestTotal,
      count: highestCount
    };
  };

  const getDailyAverage = () => {
    if (insightsTransactions.length === 0) return { calendarAverage: 0, activeDaysAverage: 0, activeDays: 0 };

    // Calculate total spent from transactions in current period
    const totalSpent = insightsTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);

    // Calculate unique days with transactions
    const uniqueDays = new Set(
      insightsTransactions.map(transaction => new Date(transaction.datetime).toDateString())
    ).size;

    // Calculate days in the selected month/year
    const daysInPeriod = new Date(year, month + 1, 0).getDate();

    return {
      calendarAverage: totalSpent / daysInPeriod, // Total spent ÷ total days in month
      activeDaysAverage: totalSpent / uniqueDays, // Total spent ÷ days with transactions
      activeDays: uniqueDays
    };
  };

  const getAverageTransactionSize = () => {
    if (insightsTransactions.length === 0) return 0;
    return insightsTransactions.reduce((sum, transaction) => sum + transaction.amount, 0) / insightsTransactions.length;
  };

  const getSpendingDistribution = () => {
    if (insightsTransactions.length === 0) return { small: 0, medium: 0, large: 0 };

    let small = 0; // <$10
    let medium = 0; // $10-50
    let large = 0; // >$50

    insightsTransactions.forEach(transaction => {
      if (transaction.amount < 10) small++;
      else if (transaction.amount <= 50) medium++;
      else large++;
    });

    return { small, medium, large };
  };

  const getMostExpensiveDay = () => {
    if (insightsTransactions.length === 0) return { date: null, amount: 0 };

    // Group transactions by date
    const dailyTotals = insightsTransactions.reduce((acc, transaction) => {
      const date = new Date(transaction.datetime).toDateString();
      acc[date] = (acc[date] || 0) + transaction.amount;
      return acc;
    }, {} as Record<string, number>);

    // Find the day with highest spending
    let maxAmount = 0;
    let maxDate = '';

    for (const [date, amount] of Object.entries(dailyTotals)) {
      if (amount > maxAmount) {
        maxAmount = amount;
        maxDate = date;
      }
    }

    return {
      date: maxDate ? new Date(maxDate) : null,
      amount: maxAmount
    };
  };

  const mostCommonNoteData = getMostCommonNote();
  const highestValueTransaction = getHighestValueTransaction();
  const highestCollectiveData = getHighestCollectiveTransactions();
  const dailyAverageData = getDailyAverage();
  const averageTransactionSize = getAverageTransactionSize();
  const spendingDistribution = getSpendingDistribution();
  const mostExpensiveDay = getMostExpensiveDay();

  // Render icon - handle both Font Awesome classes and emoji
  const renderIcon = () => {
    if (!budget) return null;
    if (budget.icon.startsWith('fas fa-') || budget.icon.startsWith('far fa-') || budget.icon.startsWith('fab fa-')) {
      return <i className={budget.icon}></i>;
    }
    return budget.icon;
  };

  // Handle transaction click
  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
  };

  // Handle transaction overlay close
  const handleTransactionOverlayClose = () => {
    setSelectedTransaction(null);
  };

  // Handle edit transaction
  const handleEditTransaction = () => {
    setShowEditOverlay(true);
    // Keep selectedTransaction for the edit overlay
  };

  // Handle edit overlay close
  const handleEditOverlayClose = () => {
    setShowEditOverlay(false);
    setSelectedTransaction(null); // Close both overlays
  };

  // Handle transaction save
  const handleTransactionSave = async (updatedTransaction: Transaction) => {
    setShowEditOverlay(false);
    setSelectedTransaction(null);
    // Reload transactions to reflect the changes
    await loadBudgetAndTransactions(year, month);
  };

  // Handle delete transaction
  const handleDeleteTransaction = () => {
    setTransactionToDelete(selectedTransaction); // Store the transaction to delete
    setSelectedTransaction(null); // Close the overlay
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return;

    setShowDeleteConfirm(false);
    
    try {
      await deleteTransaction(transactionToDelete._id);
      setTransactionToDelete(null);
      // Reload transactions
      await loadBudgetAndTransactions(year, month);
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      alert('Failed to delete transaction. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    // Reopen the transaction overlay with the transaction that was being deleted
    if (transactionToDelete) {
      setSelectedTransaction(transactionToDelete);
      setTransactionToDelete(null);
    }
  };

  const handleBackToHome = () => {
    // Check if we came from the savings page
    const source = searchParams.get('source');
    
    if (source === 'savings') {
      // Navigate back to savings page with current year/month
      const params = new URLSearchParams();
      params.set('year', year.toString());
      params.set('month', month.toString());
      navigate(`/savings?${params.toString()}`);
    } else {
      // Navigate back to home with current year/month to preserve selection
      const params = new URLSearchParams();
      params.set('year', year.toString());
      params.set('month', month.toString());
      navigate(`/?${params.toString()}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center">
        <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner size="lg" />
              <p className="text-gray-500 text-sm">Loading budget details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !budget) {
    return (
      <div className="min-h-screen flex flex-col items-center">
        <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-500 text-sm mb-2">{error || 'Budget not found'}</p>
              <button
                onClick={handleBackToHome}
                className="text-green-600 text-sm font-medium hover:text-green-700"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center">
      <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 pb-2 px-4 mb-4">
          <button
            onClick={handleBackToHome}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronDownIcon className="w-6 h-6 text-gray-700 rotate-90" />
          </button>
          <div className="flex-1 flex flex-col items-center">
            <img
              src="/budgie_logo_sm.png"
              alt="Budgie Logo"
              className="h-10 object-contain mx-auto"
              style={{ maxWidth: 100 }}
            />
          </div>
          <button
            onClick={() => setShowOverlay(true)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Add Transaction"
          >
            <PlusIcon className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        {/* Dropdowns */}
        <div className="flex-shrink-0 flex gap-2 px-6 mb-6">
          <NativePillSelect
            value={year.toString()}
            options={years.map(y => y.toString())}
            onChange={v => setYear(Number(v))}
          />
          <NativePillSelect
            value={months[month]}
            options={months}
            onChange={v => setMonth(months.indexOf(v))}
          />
        </div>

        {/* Budget Info */}
        <div className="flex-shrink-0 px-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8F9FA]">
              <div className="text-sm">{renderIcon()}</div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{budget.name}</div>
          </div>
          
          {/* Budget Progress */}
          <div className="flex justify-between items-end mb-2">
            <div className="text-base font-semibold text-gray-600">
              {budget.budgetType === 'Savings' ? (
                <div>
                  <div className="text-base font-semibold text-gray-600 mb-1">
                    ${budget.spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved overall
                  </div>
                  <div className="text-base font-semibold text-gray-600">
                    ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${budget.spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${budget.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) left to save this month
                  </div>
                </div>
              ) : budget.budgetType === 'Expense Fund' && searchParams.get('source') === 'savings' ? (
                <div>
                  <div className="text-base font-semibold text-gray-600 mb-1">
                    ${((budget.savingsAmount || 0) - (budget.expenseAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved overall
                  </div>
                  <div className="text-base font-semibold text-gray-600 mb-1">
                    ${(() => {
                      const periodValue = budget.expensePeriodValue || 1;
                      const periodUnit = budget.expensePeriodUnit || 'Months';
                      const monthlyAmount = periodUnit === 'Years' 
                        ? budget.amount / (periodValue * 12)
                        : budget.amount / periodValue;
                      const monthlySaved = budget.savingsAmount || 0;
                      const remaining = Math.max(0, monthlyAmount - monthlySaved);
                      return remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    })()} left to save this month
                  </div>
                  <div className="text-base font-semibold text-gray-600">
                    {(() => {
                      const netSaved = (budget.savingsAmount || 0) - (budget.expenseAmount || 0);
                      const totalPeriodAmount = budget.amount;
                      if (netSaved < 0) {
                        return (
                          <span className="text-red-600">
                            Under funded by ${Math.abs(netSaved).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        );
                      } else {
                        return (
                          <span>
                            ${netSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} of ${totalPeriodAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved
                          </span>
                        );
                      }
                    })()}
                  </div>
                </div>
              ) : budget.budgetType === 'Expense Fund' ? (
                <span>
                  <span className={remaining >= 0 ? 'text-emerald-600' : 'text-red-600'}>${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} available</span>
                  <span className="text-gray-700 font-semibold ml-2">
                    (${budget.savingsAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'} saved - ${budget.expenseAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'} spent all time)
                  </span>
                </span>
              ) : isOverspent ? (
                <span>
                  <span className="text-red-600">${Math.abs(remaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} overspent</span><span className="text-gray-700 font-semibold ml-2">(${budget.spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${budget.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent)</span>
                </span>
              ) : (
                <span>
                  <span className={percentageRemaining <= 15 ? 'text-yellow-600' : 'text-emerald-600'}>${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left</span><span className="text-gray-700 font-semibold ml-2">(${budget.spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${budget.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent)</span>
                </span>
              )}
            </div>
          </div>
          <div className="w-full h-2 bg-gray-300 rounded-full overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all ${progressColor}`}
              style={{ 
                width: `${Math.min(100, Math.max(0, percentageRemaining))}%`,
                left: '0'
              }}
            />
          </div>

          {/* Tabs */}
          <div className="mt-6">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('transactions')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'transactions'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <CurrencyDollarIcon className="w-4 h-4" />
                Transactions
              </button>
              <button
                onClick={() => setActiveTab('insights')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'insights'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <LightBulbIcon className="w-4 h-4" />
                Insights
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {activeTab === 'transactions' ? (
            /* Transactions List */
            <div className="flex flex-col gap-3 px-6 pb-28 pt-6 bg-[#F8F9FA] mb-4 rounded-xl">
          
          {/* Search Input */}
          <div className="relative mb-3">
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white rounded-full px-6 py-2.5 text-base font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all shadow-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {transactions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-2">No transactions found</p>
                <p className="text-gray-400 text-xs">Transactions will appear here</p>
              </div>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-2">
                  {searchTerm ? 'No transactions match your search' : 'No transactions found'}
                </p>
                <p className="text-gray-400 text-xs">
                  {searchTerm ? 'Try adjusting your search terms' : 'Transactions will appear here'}
                </p>
              </div>
            </div>
          ) : (
            filteredTransactions.map(transaction => (
              <TransactionItem 
                key={transaction._id} 
                transaction={transaction} 
                onClick={() => handleTransactionClick(transaction)}
              />
            ))
          )}
            </div>
          ) : (
            /* Insights */
            <div className="px-6 pb-28 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Total Transactions Card */}
                <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="flex flex-col">
                    <div className="text-sm text-gray-500 font-medium mb-2">Total Transactions</div>
                    <div className="text-base font-semibold text-gray-900">
                      {insightsTransactions.length}
                    </div>
                  </div>
                </div>

                {/* Most Common Transaction Card */}
                <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="flex flex-col">
                    <div className="text-sm text-gray-500 font-medium mb-2">Most Common Transaction</div>
                    <div className="text-base font-semibold text-gray-900">
                      {mostCommonNoteData.note === 'None' ? (
                        <span className="text-gray-400">N/A</span>
                      ) : (
                        <span className="text-gray-700">"{mostCommonNoteData.note}"</span>
                      )}
                    </div>
                    {mostCommonNoteData.note !== 'None' && (
                      <div className="space-y-1 mt-1">
                        <div className="text-sm text-gray-600">
                          Total spent: <span className="font-semibold text-gray-900">${mostCommonNoteData.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Appears <span className="font-semibold text-gray-900">{mostCommonNoteData.count}</span> {mostCommonNoteData.count === 1 ? 'time' : 'times'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Highest Collective Transactions Card */}
                <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="flex flex-col">
                    <div className="text-sm text-gray-500 font-medium mb-2">Highest Collective Spending</div>
                    <div className="text-base font-semibold text-gray-900">
                      {highestCollectiveData.note === 'None' ? (
                        <span className="text-gray-400">N/A</span>
                      ) : (
                        <span className="text-gray-700">"{highestCollectiveData.note}"</span>
                      )}
                    </div>
                    {highestCollectiveData.note !== 'None' && (
                      <div className="space-y-1 mt-1">
                        <div className="text-sm text-gray-600">
                          Total spent: <span className="font-semibold text-gray-900">${highestCollectiveData.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Across <span className="font-semibold text-gray-900">{highestCollectiveData.count}</span> {highestCollectiveData.count === 1 ? 'transaction' : 'transactions'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              {/* Highest Single Spend Card */}
              <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col">
                  <div className="text-sm text-gray-500 font-medium mb-2">Highest Single Spend</div>
                  <div className="text-base font-semibold text-gray-900">
                    {highestValueTransaction ? (
                      highestValueTransaction.note ? (
                        <span className="text-gray-700">"{highestValueTransaction.note}"</span>
                      ) : (
                        <span className="text-gray-400">No note</span>
                      )
                    ) : (
                      <span className="text-gray-400">None</span>
                    )}
                  </div>
                  {highestValueTransaction && (
                    <div className="space-y-1 mt-1">
                      <div className="text-sm text-gray-600">
                        Amount: <span className="font-semibold text-gray-900">${highestValueTransaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Date: <span className="font-semibold text-gray-900">
                          {new Date(highestValueTransaction.datetime).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Average Transaction Size Card */}
              <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col">
                  <div className="text-sm text-gray-500 font-medium mb-2">Avg Transaction</div>
                  <div className="text-base font-semibold text-gray-900">
                    ${averageTransactionSize.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    typical purchase size
                  </div>
                </div>
              </div>

              {/* Most Expensive Day Card */}
              <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col">
                  <div className="text-sm text-gray-500 font-medium mb-2">Most Expensive Day</div>
                  {mostExpensiveDay.date ? (
                    <>
                      <div className="text-base font-semibold text-gray-900">
                        ${mostExpensiveDay.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {mostExpensiveDay.date.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="text-base font-semibold text-gray-400">None</div>
                  )}
                </div>
              </div>

              {/* Daily Average Card */}
              <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col">
                  <div className="text-sm text-gray-500 font-medium mb-2">Daily Average</div>
                  <div className="space-y-2">
                    <div>
                      <div className="text-base font-semibold text-gray-900">
                        ${dailyAverageData.activeDaysAverage.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        over the {dailyAverageData.activeDays} day{dailyAverageData.activeDays !== 1 ? 's' : ''} that had transactions
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Spending Distribution Card */}
              <div className="col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col">
                  <div className="text-sm text-gray-500 font-medium mb-2">Purchase Sizes</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-800">Small (&lt;$10)</span>
                      <span className="font-semibold text-gray-900">{spendingDistribution.small}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-800">Medium ($10-50)</span>
                      <span className="font-semibold text-gray-900">{spendingDistribution.medium}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-800">Large (&gt;$50)</span>
                      <span className="font-semibold text-gray-900">{spendingDistribution.large}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
          )}
        </div>
      </div>

      {/* Transaction Overlay */}
      {selectedTransaction && (
        <TransactionOverlay
          transaction={selectedTransaction}
          onClose={handleTransactionOverlayClose}
          onDelete={handleDeleteTransaction}
          onEdit={handleEditTransaction}
        />
      )}

      {/* Edit Transaction Overlay */}
      {showEditOverlay && selectedTransaction && (
        <EditTransactionOverlay
          transaction={{
            ...selectedTransaction,
            budget: selectedTransaction.budget || budgetId || ''
          }}
          onClose={handleEditOverlayClose}
          onSave={handleTransactionSave}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete Transaction"
        message={`Are you sure you want to delete this transaction? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />

      {/* Quick Add Overlay */}
      {showOverlay && budget && (
        <QuickAddOverlay
          budgets={[budget]}
          initialBudget={budget}
          onClose={() => setShowOverlay(false)}
          onTransactionCreated={() => {
            setShowOverlay(false);
            // Reload budget to show updated amounts
            loadBudgetAndTransactions(year, month);
          }}
          transactionType={budget.budgetType === 'Savings' || (budget.budgetType === 'Expense Fund' && searchParams.get('source') === 'savings') ? 'savings' : 'expense'}
          selectedYear={year}
          selectedMonth={month}
        />
      )}
    </div>
  );
} 